import {
  Button,
  EmptyState,
  PickList,
  Radio,
  RadioGroup,
  Skeleton,
  Surface,
  toast,
  type PickListItem,
} from '@westhetics/ui'
import { useMemo, useState } from 'react'
import { LoadError } from '@/app/load-error'
import { useCatalogOptions } from '@/features/catalog/api'
import { errorMessage } from '@/lib/errors'
import { useSetTemplateProcedures, type Template } from './api'

/**
 * Aba Procedimentos: a base procedimento × contrato pelo lado do documento (RN-ADM-73). Cada item
 * do catálogo da Westhetics que carrega este documento, obrigatório ou sugerido. O que está aqui
 * chega sozinho a toda clínica que vende o procedimento (RN-DOC-43).
 */

interface Link {
  id: string
  name: string
  isRequired: boolean
}

export function TemplateProceduresTab({ template }: { template: Template }) {
  // O formulário nasce do que está salvo; depois de salvar, remonta com o novo.
  return (
    <Form
      key={template.procedures.map((p) => `${p.id}:${p.isRequired}`).join('|')}
      template={template}
    />
  )
}

function Form({ template }: { template: Template }) {
  const catalog = useCatalogOptions()
  const save = useSetTemplateProcedures(template.id)
  const [links, setLinks] = useState<Link[]>(template.procedures)
  const [adding, setAdding] = useState(false)
  const isArchived = template.state === 'archived'

  const saved = JSON.stringify(template.procedures)
  const dirty = JSON.stringify(links) !== saved

  const items = useMemo<PickListItem[]>(
    () =>
      (catalog.data ?? []).map((option) => ({
        id: option.id,
        label: option.name,
        group: option.groupName,
        keywords: option.aliases,
      })),
    [catalog.data],
  )

  function choose(ids: string[]) {
    setLinks((current) =>
      ids.map((id) => {
        const existing = current.find((link) => link.id === id)
        if (existing) return existing
        const option = catalog.data?.find((item) => item.id === id)
        return { id, name: option?.name ?? id, isRequired: true }
      }),
    )
  }

  function submit() {
    save.mutate(
      links.map((link) => ({ catalogProcedureId: link.id, isRequired: link.isRequired })),
      {
        onSuccess: () => {
          setAdding(false)
          toast(
            links.length === 0
              ? `"${template.name}" não acompanha mais nenhum procedimento`
              : `Vínculos salvos — o documento acompanha ${links.length === 1 ? '1 procedimento' : `${links.length} procedimentos`}`,
          )
        },
        onError: (error) => toast(errorMessage(error)),
      },
    )
  }

  return (
    <div className="flex max-w-[840px] flex-col gap-4">
      <p className="font-body text-secondary text-[13px] leading-[1.7] font-light">
        Este documento entra em todo atendimento que inclui um dos procedimentos abaixo, em qualquer
        clínica. Obrigatório entra sem opção de tirar; sugerido aparece primeiro entre os termos
        disponíveis.
      </p>

      {links.length === 0 && !adding ? (
        <EmptyState
          title="Nenhum procedimento usa este documento"
          description="Sem vínculo, o termo só chega a uma clínica se a Westhetics o ativar à mão. Ligado a um procedimento do catálogo, ele acompanha o procedimento em toda clínica que o vende."
          action={
            isArchived ? undefined : (
              <Button onClick={() => setAdding(true)}>Vincular procedimento</Button>
            )
          }
        />
      ) : (
        <Surface padding="none">
          {links.map((link) => (
            <div
              key={link.id}
              className="border-hairline flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b px-4 py-3 last:border-b-0"
            >
              <span className="font-display text-lead text-ink font-normal">{link.name}</span>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <RadioGroup
                  aria-label={`Como ${link.name} usa este documento`}
                  className="flex gap-5"
                  value={link.isRequired ? 'required' : 'suggested'}
                  disabled={isArchived}
                  onValueChange={(value) =>
                    setLinks((current) =>
                      current.map((item) =>
                        item.id === link.id ? { ...item, isRequired: value === 'required' } : item,
                      ),
                    )
                  }
                >
                  <label className="font-body text-body text-ink min-h-touch-desk max-desk:min-h-touch flex cursor-pointer items-center gap-2 font-light">
                    <Radio value="required" />
                    Obrigatório
                  </label>
                  <label className="font-body text-body text-ink min-h-touch-desk max-desk:min-h-touch flex cursor-pointer items-center gap-2 font-light">
                    <Radio value="suggested" />
                    Sugerido
                  </label>
                </RadioGroup>
                {isArchived ? null : (
                  <button
                    type="button"
                    onClick={() =>
                      setLinks((current) => current.filter((item) => item.id !== link.id))
                    }
                    className="font-body text-eyebrow text-label hover:text-ink min-h-touch-desk max-desk:min-h-touch cursor-pointer font-light tracking-normal"
                    aria-label={`Remover ${link.name}`}
                  >
                    remover
                  </button>
                )}
              </div>
            </div>
          ))}
        </Surface>
      )}

      {adding ? (
        catalog.isError ? (
          <LoadError
            title="Não conseguimos carregar o catálogo de procedimentos"
            error={catalog.error}
            onRetry={() => void catalog.refetch()}
          />
        ) : catalog.isPending ? (
          <div role="status" aria-label="Carregando">
            <Skeleton className="h-[240px] w-full" />
          </div>
        ) : (
          <PickList
            label="Procedimentos do catálogo da Westhetics"
            items={items}
            value={links.map((link) => link.id)}
            onChange={choose}
            searchPlaceholder="Buscar procedimento do catálogo"
            autoFocus
            emptySearch={(query) =>
              `Nenhum procedimento do catálogo com "${query}". Crie o item em Catálogo de procedimentos e volte aqui.`
            }
          />
        )
      ) : null}

      {isArchived ? null : (
        <div className="flex flex-wrap justify-end gap-3">
          {links.length > 0 && !adding ? (
            <Button variant="outline" onClick={() => setAdding(true)}>
              + Vincular procedimento
            </Button>
          ) : null}
          <Button
            disabled={!dirty}
            loading={save.isPending}
            loadingLabel="Salvando os vínculos…"
            onClick={submit}
          >
            {dirty ? 'Salvar vínculos' : 'Nenhuma alteração para salvar'}
          </Button>
        </div>
      )}
    </div>
  )
}
